import { inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Observable, catchError, of, switchMap } from 'rxjs';
import { StateManager } from '@shared/services/state-manager/state-manager.service';
import { LOCALES } from '../../static/locales';

export const DEFAULT_LANGUAGE = 'en';

/** Returns the saved language when it is supported, otherwise English. */
export function resolvePreferredLanguage(stateManager: StateManager): string {
  const saved = stateManager['user-locale']?.language;
  return LOCALES.some(locale => locale.id === saved) ? saved : DEFAULT_LANGUAGE;
}

/**
 * App initializer: registers English as the fallback for missing keys and waits for the
 * preferred language file, so the first render never shows raw translation keys.
 */
export function initializeTranslations(): Observable<unknown> {
  const translate = inject(TranslateService);
  const preferredLanguage = resolvePreferredLanguage(inject(StateManager));
  // setDefaultLang() starts loading but returns void in ngx-translate 14.
  // Load first so both the fallback and selected dictionary are ready before rendering.
  return translate.getTranslation(DEFAULT_LANGUAGE).pipe(
    switchMap(() => {
      translate.setDefaultLang(DEFAULT_LANGUAGE);
      if (preferredLanguage === DEFAULT_LANGUAGE) return translate.use(DEFAULT_LANGUAGE);
      return translate.getTranslation(preferredLanguage).pipe(
        switchMap(() => translate.use(preferredLanguage)),
        catchError(() => translate.use(DEFAULT_LANGUAGE))
      );
    }),
    // Retain non-blocking startup if the canonical dictionary itself is unavailable.
    catchError(() => of(null))
  );
}
